/* El estado de una guía de encomiendas, derivado de sus bultos.
 *
 * ── POR QUÉ ESTÁ COPIADO Y NO IMPORTADO ────────────────────────────────────
 *
 * Esto es un port de `src/logica/estado.js` del repo `fleet-encomiendas`. Los
 * dos repos comparten el proyecto de Supabase pero NO comparten código: uno es
 * Vite y el otro Create React App, y no hay paquete común entre ellos. Sacar
 * un paquete npm para cincuenta líneas sería un tercer lugar donde mirar.
 *
 * Lo que sí importa es que si allá cambia la regla, acá cambia también —y la
 * única forma de que eso no se olvide es que esté escrito. Está escrito acá.
 *
 * ── LA REGLA, QUE NO ES OBVIA ──────────────────────────────────────────────
 *
 * El estado del bulto se materializa en `enc_bultos.estado`; el de la GUÍA no
 * se guarda en ningún lado, se calcula al leer. Un estado guardado aparte es
 * una copia que un día difiere del event-log, y ante la discrepancia manda el
 * log — así que mejor no tener copia. Con diez guías por día, calcularlo al
 * leer no cuesta nada.
 *
 * «Parcial» existe a propósito y es la razón de ser de esta pantalla: una guía
 * con 8 bultos en el camión y 2 en el depósito NO está «en viaje», está a
 * medias. Decirlo es lo que evita que esos 2 se queden en Buenos Aires sin que
 * nadie pregunte por ellos. El monitor ordena por eso, no por fecha.
 */

export const ETIQUETA_BULTO = {
  recibido: "en depósito",
  en_camion: "en el camión",
  en_destino: "en destino",
  entregado: "entregado",
  retirado: "retirado",
};

const FINALES = new Set(["entregado", "retirado"]);

/* El orden en que la oficina tiene que mirar las guías. No es el orden de la
 * operación —«en depósito» viene antes que «en viaje» en el mundo real— es el
 * orden de QUIÉN NECESITA UNA RESPUESTA HOY: la guía partida primero, la
 * terminada al fondo. */
export const ORDEN_ESTADOS = [
  "parcial",
  "en destino",
  "en viaje",
  "en depósito",
  "sin bultos",
  "finalizada",
];

export function estadoDeGuia(bultos) {
  const es = (bultos || []).map((b) => b.estado);
  if (!es.length) return "sin bultos";
  const unicos = new Set(es);
  if (es.every((e) => FINALES.has(e))) return "finalizada";
  if (unicos.size === 1) {
    const e = es[0];
    if (e === "recibido") return "en depósito";
    if (e === "en_camion") return "en viaje";
    if (e === "en_destino") return "en destino";
  }
  /* Mezcla: la palabra importa — «parcial» es una pregunta, no un estado
   * intermedio prolijo. */
  return "parcial";
}

/* Cuántos bultos llegaron a destino final, sobre el total. */
export function avance(bultos) {
  const total = (bultos || []).length;
  const listos = (bultos || []).filter((b) => FINALES.has(b.estado)).length;
  return { listos, total };
}

/* La guía anulada no es un estado más: es una guía que dejó de existir para la
 * operación. No compite con «parcial» por la atención de nadie, así que sale
 * de la lista en vez de pintarse de gris al lado de las vivas. */
export const estaAnulada = (guia) => Boolean(guia && guia.anulado_en);

/* Lo que la pantalla arma una sola vez: cada guía con su estado derivado y su
 * avance, ordenadas por urgencia y, dentro de un mismo estado, la más nueva
 * primero.
 *
 * Es una función pura y tiene test porque acá vive la única cuenta del sector
 * que alguien podría leer mal sin darse cuenta: una guía sin bultos cargados
 * todavía y una guía terminada son las dos «no me molestes», pero por motivos
 * opuestos. */
export function panel(guias, bultosPorGuia) {
  const vivas = (guias || []).filter((g) => !estaAnulada(g));
  const filas = vivas.map((g) => {
    const bultos = (bultosPorGuia && bultosPorGuia[g.id]) || [];
    return {
      ...g,
      bultos,
      estado: estadoDeGuia(bultos),
      avance: avance(bultos),
    };
  });
  filas.sort((a, b) => {
    const pa = ORDEN_ESTADOS.indexOf(a.estado);
    const pb = ORDEN_ESTADOS.indexOf(b.estado);
    if (pa !== pb) return pa - pb;
    return String(b.creado_en || "").localeCompare(String(a.creado_en || ""));
  });
  return filas;
}

/* Los números de arriba. Se cuentan sobre las filas YA derivadas para que el
 * total del encabezado y lo que se ve en la tabla no puedan diferir nunca:
 * dos cuentas separadas de lo mismo terminan discrepando el día que alguien
 * agrega un filtro y se olvida de la otra. */
export function resumen(filas) {
  const por = {};
  let bultos = 0;
  (filas || []).forEach((f) => {
    por[f.estado] = (por[f.estado] || 0) + 1;
    bultos += f.avance ? f.avance.total : 0;
  });
  return {
    guias: (filas || []).length,
    bultos,
    por,
    enCamino: (por["en viaje"] || 0) + (por["parcial"] || 0),
  };
}
